import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { LightBulbIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import TalkToAdvisorButton from './advice/TalkToAdvisorButton';
import { useAdviceSync } from '../context/AdviceSyncContext';

const mapLedgerRec = (rec) => {
  if (!rec) return null;
  return {
    title: rec.title,
    body: rec.description || rec.reason || rec.action || '',
    cta: rec.action || 'View details',
    priority: rec.priority || 'medium',
    riskLevel: rec.risk_level,
    type: rec.type,
    isAI: true,
    recommendationId: rec.recommendation_id || rec.proposal_id,
  };
};

const NextBestAction = ({ portfolioData, news }) => {
  const { recommendations: syncedRecs, focusLine, reload: reloadAdvice, loading: syncLoading } = useAdviceSync();
  const [ledgerData, setLedgerData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadLedger = useCallback(async () => {
    try {
      const res = await axios.get('/api/advice/surface/news', { timeout: 10000 });
      if (res.data?.success) {
        setLedgerData(res.data);
        return res.data;
      }
    } catch (err) {
      console.warn('Ledger read failed:', err);
    }
    return null;
  }, []);

  useEffect(() => {
    const fetchRecommendations = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (news && news.length > 0) {
          await axios.post('/api/news/next-best-actions', {
            news_articles: news,
            user_portfolio: portfolioData,
          });
        }
        await loadLedger();
        await reloadAdvice();
      } catch (err) {
        setError('Failed to load recommendations');
        await loadLedger();
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecommendations();
  }, [news, portfolioData, loadLedger, reloadAdvice]);

  const getBestRecommendation = () => {
    if (isLoading) {
      return {
        title: 'Loading recommendations…',
        body: 'Reading from your unified advice ledger.',
        cta: 'Please wait',
        priority: 'medium',
        isLoading: true,
      };
    }

    const recs = syncedRecs?.length
      ? syncedRecs
      : ledgerData?.recommendations || [];
    const top = mapLedgerRec(recs[0]);
    if (top) {
      return top;
    }

    if (error) {
      return {
        title: 'No ledger recommendations yet',
        body: 'Connect accounts or add cashflow data so Aura can build your advice ledger.',
        cta: 'Refresh',
        priority: 'low',
        isError: true,
      };
    }

    return {
      title: 'Advice ledger empty',
      body: 'Aura has no active recommendations. Add financial data to get personalized guidance.',
      cta: 'Refresh',
      priority: 'low',
    };
  };

  const rec = getBestRecommendation();
  const otherRecs = (syncedRecs?.length ? syncedRecs : ledgerData?.recommendations || []).slice(1, 3);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-danger';
      case 'medium': return 'text-warning';
      case 'low': return 'text-positive';
      default: return 'text-pine';
    }
  };

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case 'high': return 'text-danger';
      case 'medium': return 'text-warning';
      case 'low': return 'text-positive';
      default: return 'text-secondary';
    }
  };

  return (
    <motion.div
      className="surface-panel p-5"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <LightBulbIcon className="w-5 h-5 text-pine mr-2" />
          <h3 className="text-primary font-display font-semibold">Next best action</h3>
          {rec.isAI && (
            <span className="ml-2 topic-tag text-xs">
              Ledger
            </span>
          )}
        </div>
        {(isLoading || syncLoading) && (
          <ArrowPathIcon className="w-4 h-4 text-pine animate-spin" />
        )}
      </div>

      {focusLine && (
        <p className="text-label text-pine mb-2 line-clamp-2">{focusLine}</p>
      )}

      <p className="text-secondary mb-3">{rec.title ? `${rec.title} — ${rec.body}` : rec.body}</p>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            className="btn-primary px-4 py-2"
            disabled={rec.isLoading}
            onClick={() => {
              loadLedger();
              reloadAdvice();
            }}
          >
            {rec.cta}
          </button>

          {rec.isError && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-ghost ml-2 text-xs"
            >
              Retry
            </button>
          )}
        </div>

        <div className={`text-xs ${getPriorityColor(rec.priority)}`}>
          {rec.priority?.toUpperCase()} PRIORITY
          {rec.riskLevel && (
            <span className={`ml-2 ${getRiskLevelColor(rec.riskLevel)}`}>
              {rec.riskLevel.toUpperCase()} RISK
            </span>
          )}
        </div>
      </div>

      {otherRecs.length > 0 && (
        <div className="mt-3 pt-3 border-t border-hairline">
          <div className="text-xs text-secondary mb-2">Other ledger recommendations:</div>
          {otherRecs.map((item, index) => (
            <div key={item.recommendation_id || index} className="text-xs text-primary mb-1">
              • {item.title}: {item.reason || item.description}
            </div>
          ))}
        </div>
      )}

      <TalkToAdvisorButton
        userQuestion={rec.title ? `${rec.title} — ${rec.body}` : rec.body}
        surface="news"
        framing={rec.title || rec.body}
        compact
      />
    </motion.div>
  );
};

export default NextBestAction;
